import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Sparkles, CheckCircle2, AlertTriangle, ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import api from '../../../api';

export default function AiTaxAdvisor({ fiscalData, calculations }) {
  const [loading, setLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [error, setError] = useState(null);

  const handleAskAi = async () => {
    setLoading(true);
    setError(null);

    const payload = {
      formData: {
        statut: fiscalData.statut,
        enfants: fiscalData.enfants,
        revenus: calculations.revenuBrutGlobal,
        deductions: fiscalData.versementPer || 0,
        foncier: fiscalData.loyersBruts || 0,
        dividendes: fiscalData.dividendesBruts || 0
      },
      calcData: {
        parts: calculations.parts,
        baseImposable: calculations.baseImposable,
        quotientFamilial: calculations.quotientFamilial,
        tmi: calculations.tmi,
        impotTotal: calculations.totalImpotGlobal
      }
    };

    try {
      const response = await api.post('/ai/optimize-taxes', payload);
      if (response.data?.success) {
        setAiResult(response.data.data);
      } else {
        setError("L'assistant IA n'a pas pu traiter cette configuration. Veuillez réessayer.");
      }
    } catch (err) {
      console.error("Erreur Appel IA Impôts :", err);
      setError("Impossible de contacter le serveur d'intelligence artificielle locale.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-workspace mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-primary">Assistant IA d'Ingénierie Fiscale</h3>
            <p className="text-xs text-subtle">Audit des inefficiences fiscales et préconisations de défiscalisation sur-mesure.</p>
          </div>
        </div>

        <Button
          variant="accent"
          size="sm"
          onClick={handleAskAi}
          disabled={loading}
          className="gap-2 shadow-hp-sm font-semibold"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Analyse en cours...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Générer l'Audit IA
            </>
          )}
        </Button>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-danger-50 text-danger-700 dark:bg-danger-900/20 dark:text-danger-300 border border-danger-200 dark:border-danger-800 text-xs flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {aiResult ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          {aiResult.tmiAnalysis && (
            <div className="p-4 rounded-xl bg-secondary/5 border border-secondary/20 text-xs text-primary leading-relaxed font-medium">
              💡 <strong>Diagnostic Global :</strong> {aiResult.tmiAnalysis}
            </div>
          )}

          {Array.isArray(aiResult.optimizations) && aiResult.optimizations.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {aiResult.optimizations.map((opt, i) => (
                <div key={i} className="p-4 rounded-xl bg-surface-muted/30 border border-workspace flex flex-col justify-between">
                  <div>
                    <h4 className="text-sm font-bold text-primary mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-success-500 shrink-0" />
                      {opt.title}
                    </h4>
                    <p className="text-xs text-subtle leading-relaxed">{opt.description}</p>
                  </div>
                  {opt.estimatedGain && (
                    <div className="mt-4 pt-3 border-t border-workspace text-xs font-bold text-success-600">
                      Gain estimé : {opt.estimatedGain}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {aiResult.warnings && (
            <div className="p-3.5 rounded-xl bg-warning-50 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300 border border-warning-200 dark:border-warning-800 text-xs">
              ⚠️ {aiResult.warnings}
            </div>
          )}
        </motion.div>
      ) : (
        <div className="py-12 flex flex-col items-center justify-center text-center text-subtle text-xs gap-3">
          <Sparkles className="w-8 h-8 text-subtle opacity-40" />
          <p className="max-w-md">
            Cliquez sur <strong>"Générer l'Audit IA"</strong> pour interroger le modèle Llama sur la situation fiscale exacte du client et découvrir des pistes d'arbitrage.
          </p>
        </div>
      )}
    </div>
  );
}

